import React, { useState } from 'react';
import {
  Button,
  Typography
} from "@material-tailwind/react";
import { CheckIcon, XIcon } from "lucide-react";
import { ModalX } from '../components';
/**
 * A confirmation dialog component similar to window.confirm() using Material Tailwind
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.setIsOpen - Function to set open state
 * @param {React.ReactElement | string} props.title - Dialog title
 * @param {React.ReactElement | string} props.message - Dialog message
 * @param {string} props.confirmText - Text for confirm button (defaults to "Confirm")
 * @param {string} props.cancelText - Text for cancel button (defaults to "Cancel") 
 * @param {Function} props.onConfirm - Function to call when confirmed
 * @param {Function} props.onCancel - Function to call when canceled
 * @returns {React.ReactElement}
 */
const ConfirmDialog = (props) => {
  const {
    isOpen = false,
    setIsOpen = () => { },
    title = "Confirmation",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    confirmBtnColor = "black",
    confirmIcon = <CheckIcon size={12} />,
    cancelText = "Cancel",
    cancelBtnColor = "gray",
    cancelIcon = <XIcon size={12} />,
    onConfirm,
    onCancel,
    footer
  } = props || {};

  // Handle confirm action
  const handleConfirm = () => {
    setIsOpen(false);
    if (onConfirm) onConfirm();
  };

  // Handle cancel action
  const handleCancel = () => {
    setIsOpen(false);
    if (onCancel) onCancel();
  };

  return (
    <ModalX
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={title}
      maxWidth="sm"
    >
      <Typography className='whitespace-pre-wrap text-blue-gray-800 text-base font-medium'>{message}</Typography>
      <hr className='my-4' />
      <div className="space-x-2 flex justify-between">
        {footer}
        <div className="flex gap-2">
          <Button variant="text" className="rounded-full flex items-center gap-2" size='sm' color={cancelBtnColor} onClick={handleCancel}>
            {cancelIcon} {cancelText}
          </Button>
          <Button variant="filled" className="rounded-full flex items-center gap-2" size='sm' color={confirmBtnColor} onClick={handleConfirm}>
            {confirmIcon} {confirmText}
          </Button>
        </div>
      </div>
    </ModalX>
  );
};

/**
 * Custom hook to use confirmation dialogs similar to window.confirm()
 * 
 * @returns {Object} useConfirm hook
 * @property {Function} confirm - Function to show confirmation dialog
 * @property {React.ReactElement} ConfirmationDialog - Dialog component to be included in your component
 */
export const useConfirm = () => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmBtnColor: 'black',
    cancelBtnColor: 'gray',
    onConfirm: () => { },
    onCancel: () => { },
    footer: <></>
  });

  const confirm = ({
    title = 'Confirmation',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmBtnColor = 'black',
    cancelBtnColor = 'gray',
    footer = <div></div>
  } = {}) => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        confirmBtnColor,
        cancelBtnColor,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
        footer
      });
    });
  };

  const ConfirmationDialog = () => (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      setIsOpen={(open) => setDialogState(prev => ({ ...prev, isOpen: open }))}
      title={dialogState.title}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      confirmBtnColor={dialogState.confirmBtnColor}
      cancelBtnColor={dialogState.cancelBtnColor}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
      footer={dialogState.footer}
    />
  );
  return {
    confirm,
    ConfirmationDialog
  };
};

export default ConfirmDialog;